/* eslint-disable @typescript-eslint/no-explicit-any */
import { getConfig } from '@sydle/ui-utils';
import stringifyJSON from 'json-stable-stringify';
import { UserAPI } from './api/user-api';
import { APIEvents } from './enums/api-events';
import { retryInterceptor } from './interceptors/retry-interceptor';
import { mapToFetchResponse } from './utils/map-to-fetch-response';
import { sendCustomEvent } from './utils/send-custom-event';
/**
 * Default RestService that implements the http requests using fetchApi.
 */
export class DefaultRestService {
    /**
     * @param config - Service configurations.
     * @param defaultRequestParams - Default parameters for requests.
     */
    constructor(config = {}, defaultRequestParams = {}) {
        var _a, _b, _c, _d, _e, _f, _g;
        this.interceptors = [];
        this.defaultRequestParams = {
            renewToken: true,
            extractResponseData: true,
            addUserTokenOnHeader: true,
            addBaseUrl: true,
        };
        this.apiConfig = getConfig('ui-api');
        this.config = {
            baseUrl: (_b = (_a = this.apiConfig) === null || _a === void 0 ? void 0 : _a.BASE_API_URL) !== null && _b !== void 0 ? _b : '',
            app: (_d = (_c = this.apiConfig) === null || _c === void 0 ? void 0 : _c.APPLICATION_IDENTIFIER) !== null && _d !== void 0 ? _d : '',
            org: (_f = (_e = this.apiConfig) === null || _e === void 0 ? void 0 : _e.ORGANIZATION_IDENTIFIER) !== null && _f !== void 0 ? _f : '',
        };
        this.config = Object.assign(Object.assign({}, this.config), config);
        this.defaultRequestParams = Object.assign(Object.assign(Object.assign({}, this.defaultRequestParams), (_g = this.apiConfig) === null || _g === void 0 ? void 0 : _g.REQUEST_PARAMS), defaultRequestParams);
        this.registerInterceptor(retryInterceptor);
    }
    /**
     * Gets the service configurations.
     * @returns Service configurations.
     */
    getConfig() {
        return this.config;
    }
    /**
     * Performs a GET request.
     * @param params - Request parameters.
     * @returns Response of the request.
     */
    get(params) {
        const requestParams = Object.assign(Object.assign({}, params), { method: 'GET' });
        return this.fetch(requestParams);
    }
    /**
     * Performs a POST request.
     * @param params - Request parameters.
     * @returns Response of the request.
     */
    post(params) {
        const requestParams = Object.assign(Object.assign({}, params), {
            method: 'POST',
            timeout: 30 * 60 * 1000,
        });
        return this.fetch(requestParams);
    }
    /**
     * Performs a PUT request.
     * @param params - Request parameters.
     * @returns Response of the request.
     */
    put(params) {
        const requestParams = Object.assign(Object.assign({}, params), {
            method: 'PUT',
        });
        return this.fetch(requestParams);
    }
    /**
     * Performs a DELETE request.
     * @param params - Request parameters.
     * @returns Response of the request.
     */
    delete(params) {
        const requestParams = Object.assign(Object.assign({}, params), {
            method: 'DELETE',
        });
        return this.fetch(requestParams);
    }
    /**
     * Constructs an XMLHttpRequest object.
     * @param params - Request params.
     * @returns XMLHttpRequest object.
     */
    buildXHR(params) {
        const xhr = new XMLHttpRequest();
        const { requestParams } = this.getRequestParams(params);
        xhr.open('POST', requestParams.url);
        xhr.responseType = 'json';
        const headers = requestParams.headers;
        for (const [headerName, headerValue] of Object.entries(headers)) {
            if (headerName === 'Content-Type')
                continue; // TODO: getDefaultHeaders deve ser generico para utilizar aqui tb?!
            xhr.setRequestHeader(headerName, headerValue);
        }
        xhr.timeout = requestParams.timeout;
        return xhr;
    }
    /**
     * Performs an asynchronous request.
     * @param params - Request params.
     * @returns A Promise that resolves with the response of the request.
     */
    async fetch(params) {
        var _a;
        if ((_a = this.interceptors) === null || _a === void 0 ? void 0 : _a.length) {
            for (let i = 0; i < this.interceptors.length; i++) {
                const interceptorRequest = this.interceptors[i].interceptorRequest;
                if (interceptorRequest) {
                    const requestFunction = interceptorRequest.bind(this.interceptors[i]);
                    params = await requestFunction(params, !(params.addBaseUrl !== null && params.addBaseUrl !== undefined
                        ? params.addBaseUrl
                        : this.defaultRequestParams.addBaseUrl));
                }
            }
        }
        // If the user has an invalid token, the fetch must be aborted to avoid making the request with an invalid token.
        // The localStorage will be cleared, so the next request will be made with a new token or without a token.
        const userAPI = new UserAPI(this);
        if (userAPI.getUser() && userAPI.isTokenExpired()) {
            userAPI.logoutClient();
            sendCustomEvent(APIEvents.INVALID_TOKEN);
            return {
                error: { status: null, data: new Error('Fetch aborted: user has an expired token and cannot be renewed.') },
                meta: { request: null, response: null, requestParams: null },
            };
        }
        const { requestParams, options } = this.getRequestParams(params);
        let abortTimeoutId;
        if (requestParams.signal) {
            options['signal'] = requestParams.signal;
        }
        else if (requestParams.timeout) {
            const controller = new AbortController();
            const signal = controller.signal;
            abortTimeoutId = setTimeout(() => {
                controller.abort();
            }, requestParams.timeout);
            options['signal'] = signal;
        }
        const request = new Request(requestParams.url, options);
        return fetch(request.url, options)
            .then(async (response) => {
            clearTimeout(abortTimeoutId);
            return mapToFetchResponse(request, requestParams, response);
        })
            .then(async (response) => {
            var _a;
            if ((_a = this.interceptors) === null || _a === void 0 ? void 0 : _a.length) {
                for (let i = 0; i < this.interceptors.length; i++) {
                    const interceptorResponse = this.interceptors[i].interceptorResponse;
                    if (interceptorResponse) {
                        const responseFunction = interceptorResponse.bind(this.interceptors[i]);
                        response = await responseFunction(response, requestParams, this);
                    }
                }
            }
            this.verifyAndSendExpiringSessionEvent();
            this.sendCompleteRequestEvent(response);
            return response;
        })
            .catch((error) => {
            this.sendIncompleteRequestEvent(error);
            clearTimeout(abortTimeoutId);
            return {
                error: {
                    status: null,
                    data: error,
                },
                meta: { request, response: null, requestParams },
            };
        });
    }
    /**
     * Adds the base URL to the given URL.
     * @param url - URL to be modified.
     * @returns Modified URL.
     */
    addBaseUrl(url) {
        return `${this.config.baseUrl + this.config.app}/${url}`;
    }
    /**
     * Registers an interceptor in the array.
     * @param interceptor - Interceptor to be registered.
     */
    registerInterceptor(interceptor) {
        if (!this.interceptors)
            this.interceptors = [];
        if (this.interceptors.indexOf(interceptor) !== -1) {
            return;
        }
        this.interceptors.push(interceptor);
    }
    /**
     * Removes an interceptor from the array.
     * @param interceptor - Interceptor to be removed.
     */
    removeInterceptor(interceptor) {
        const index = this.interceptors.indexOf(interceptor);
        if (index > -1) {
            this.interceptors.splice(index, 1);
        }
    }
    getRequestParams(params) {
        const requestParams = Object.assign(Object.assign(Object.assign({}, this.defaultRequestParams), params), {
            headers: Object.assign(Object.assign(Object.assign({}, (this.defaultRequestParams.headers || {})), this.getDefaultHeaders()), params.headers),
        });
        if (requestParams.public) {
            delete requestParams.headers['Authorization'];
        }
        if (!requestParams.public && requestParams.addUserTokenOnHeader) {
            requestParams.headers = Object.assign(Object.assign({}, requestParams.headers), this.getHeaderToken());
        }
        if (requestParams.renewToken) {
            this.renewToken();
        }
        if (this.config.org) {
            requestParams.headers = Object.assign(Object.assign({}, requestParams.headers), this.getHeaderOrg());
        }
        if (requestParams.addBaseUrl) {
            requestParams.url = this.addBaseUrl(requestParams.url);
        }
        const options = {
            method: requestParams.method || 'GET',
            headers: requestParams.headers,
        };
        if (requestParams.body) {
            options['body'] = typeof requestParams.body === 'object' ? stringifyJSON(requestParams.body) : requestParams.body;
        }
        if (requestParams.keepalive) {
            options['keepalive'] = requestParams.keepalive;
        }
        return { requestParams, options };
    }
    getHeaderToken() {
        const userApi = new UserAPI(this);
        const user = userApi.getUser();
        if (user) {
            return { Authorization: `Bearer ${user.accessToken.token}` };
        }
    }
    getHeaderOrg() {
        if (this.config.org) {
            return { 'X-Explorer-Account-Token': this.config.org };
        }
    }
    getDefaultHeaders() {
        const userApi = new UserAPI(this);
        return {
            'Content-Type': 'application/json',
            'Accept-Language': userApi.getCurrentLanguage(this.config.language),
        };
    }
    renewToken() {
        clearTimeout(this.tokenRenewalDebounceTime);
        this.tokenRenewalDebounceTime = setTimeout(() => {
            const userApi = new UserAPI(this);
            if (userApi.isLoggedIn()) {
                const user = userApi.getUser();
                const tokenExpiration = Number(user === null || user === void 0 ? void 0 : user.accessToken.payload.exp);
                const tokenCreation = Number(user === null || user === void 0 ? void 0 : user.accessToken.payload.iat);
                const currentTimeInSeconds = Date.now() / 1000;
                if (currentTimeInSeconds > tokenCreation + (tokenExpiration - tokenCreation) * 0.85) {
                    userApi.refreshLogin();
                }
            }
        }, 500);
    }
    sendCompleteRequestEvent(fetchResponse) {
        var _a, _b, _c;
        sendCustomEvent(APIEvents.REQUEST_COMPLETE, fetchResponse);
        if ((_a = fetchResponse.meta) === null || _a === void 0 ? void 0 : _a.response.ok) {
            sendCustomEvent(APIEvents.REQUEST_COMPLETE_SUCCESS, fetchResponse);
        }
        else if (((_b = fetchResponse.error) === null || _b === void 0 ? void 0 : _b.status) === 401) {
            // UNAUTHORIZED
            sendCustomEvent(APIEvents.UNAUTHORIZED, fetchResponse);
        }
        else if (((_c = fetchResponse.error) === null || _c === void 0 ? void 0 : _c.status) === 403) {
            // FORBIDDEN
            sendCustomEvent(APIEvents.FORBIDDEN, fetchResponse);
        }
        else {
            sendCustomEvent(APIEvents.REQUEST_COMPLETE_ERROR, fetchResponse);
        }
    }
    verifyAndSendExpiringSessionEvent() {
        const userApi = new UserAPI(this);
        if (userApi.isExpiringSessionTimeout()) {
            const event = new CustomEvent(APIEvents.EXPIRING_SESSION, {
                detail: {
                    remaingSessionTimeout: userApi.remainingSessionTimeout(),
                    isLoggedIn: userApi.isLoggedIn(),
                },
            });
            window.dispatchEvent(event);
        }
    }
    sendIncompleteRequestEvent(fetchResponse) {
        sendCustomEvent(APIEvents.REQUEST_INCOMPLETE, fetchResponse);
    }
}
/**
 * @deprecated Use `APIEvents.REQUEST_COMPLETE` instead.
 */
DefaultRestService.REQUEST_COMPLETE = '@sydle/one-api:request-complete';
/**
 * @deprecated Use `APIEvents.REQUEST_INCOMPLETE` instead.
 */
DefaultRestService.REQUEST_INCOMPLETE = '@sydle/one-api:request-incomplete';
/**
 * @deprecated Use `APIEvents.REQUEST_INCOMPLETE` instead.
 */
DefaultRestService.REQUEST_COMPLETE_SUCCESS = '@sydle/one-api:request-complete-success';
/**
 * @deprecated Use `APIEvents.REQUEST_COMPLETE_ERROR` instead.
 */
DefaultRestService.REQUEST_COMPLETE_ERROR = '@sydle/one-api:request-complete-error';
/**
 * @deprecated Use `APIEvents.EXPIRING_SESSION` instead.
 */
DefaultRestService.EXPIRING_SESSION = '@sydle/one-api:expiring-session';
/**
 * @deprecated Use `APIEvents.UNAUTHORIZED` instead.
 */
DefaultRestService.UNAUTHORIZED = '@sydle/one-api:unauthorized';
/**
 * @deprecated Use `APIEvents.FORBIDDEN` instead.
 */
DefaultRestService.FORBIDDEN = '@sydle/one-api:forbidden';
